import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChartPanel,
  ComparisonCard,
  BreakdownPanel,
  PERIOD_OPTIONS,
  GROUP_BY_OPTIONS,
  selectClass,
  formatNumber,
} from "./analyticsShared";
import { useDashboardStats } from "../useHook";

const MODULES = [
  { key: "users", label: "Users", accent: "#2563EB" },
  { key: "orders", label: "Orders", accent: "#059669" },
  { key: "products", label: "Products", accent: "#982598" },
  { key: "designs", label: "House Designs", accent: "#4F46E5" },
  { key: "interiors", label: "Interior Designs", accent: "#7C3AED" },
  { key: "blogs", label: "Blogs", accent: "#0284C7" },
  { key: "projects", label: "Our Projects", accent: "#D97706" },
  { key: "contracts", label: "Contracts", accent: "#E11D48" },
  { key: "pricing", label: "Pricing", accent: "#EA580C" },
];

const getModuleMeta = (key) =>
  MODULES.find((m) => m.key === key) || MODULES[0];

const buildChartsForModule = (key, data) => {
  if (!data) return [];

  switch (key) {
    case "users":
      return [
        {
          title: data?.usersChart?.datasets?.[0]?.label || "User Growth",
          chart: data?.usersChart || data?.chart,
        },
      ].filter((c) => c.chart);
    case "orders":
      return [
        {
          title: "Orders Trend",
          chart: data?.ordersChart,
          color: "#059669",
        },
        {
          title: data?.chart?.datasets?.[0]?.label || "Products Trend",
          chart: data?.chart,
          color: "#982598",
        },
      ].filter((c) => c.chart);
    case "products":
      return [
        {
          title: data?.chart?.datasets?.[0]?.label || "Products Trend",
          chart: data?.chart,
        },
      ].filter((c) => c.chart);
    case "designs":
    case "interiors":
    case "blogs":
    case "projects":
      return [
        {
          title: data?.chart?.datasets?.[0]?.label || "Trend",
          chart: data?.chart,
        },
      ].filter((c) => c.chart);
    case "contracts":
      return [
        {
          title: data?.chart?.datasets?.[0]?.label || "Construction Contracts",
          chart: data?.chart,
        },
        {
          title: "Design Contracts",
          chart: data?.designChart,
          color: "#BE123C",
        },
        {
          title: "User Generations",
          chart: data?.userGenerationsChart,
          color: "#F43F5E",
        },
      ].filter((c) => c.chart);
    case "pricing":
      return [
        {
          title: data?.chart?.datasets?.[0]?.label || "Budget Pricing",
          chart: data?.chart,
        },
        {
          title: "Premium Pricing",
          chart: data?.premiumChart,
          color: "#F97316",
        },
      ].filter((c) => c.chart);
    default:
      return [];
  }
};

const getComparisonForModule = (key, data) => {
  if (!data) return null;

  switch (key) {
    case "users":
      return data?.comparison?.users;
    case "orders":
    case "products":
    case "designs":
    case "interiors":
    case "blogs":
    case "projects":
      return data?.comparison;
    case "contracts":
      return [
        {
          title: "Construction",
          data: data?.comparison?.construction,
        },
        {
          title: "Design Contracts",
          data: data?.comparison?.design,
        },
      ].filter((c) => c.data);
    case "pricing":
      return [
        { title: "Budget", data: data?.comparison?.budget },
        { title: "Premium", data: data?.comparison?.premium },
      ].filter((c) => c.data);
    default:
      return null;
  }
};

const getBreakdownsForModule = (key, data) => {
  if (!data?.breakdown) return [];

  if (key === "designs" && data.breakdown?.byFloors) {
    return [
      {
        title: "By Floors",
        items: data.breakdown.byFloors.map((r) => ({
          label: r.key != null ? `${r.key} Floor` : "N/A",
          count: r.count,
        })),
        labelKey: "label",
      },
    ];
  }

  if (key === "products" && data.breakdown?.byCategory) {
    return [
      {
        title: "By Category",
        items: data.breakdown.byCategory.map((r) => ({
          label: r.categoryName || "Uncategorized",
          count: r.count,
        })),
        labelKey: "label",
        valueKey: "count",
      },
    ];
  }

  if (key === "orders" && data.orders?.byStatus) {
    return [
      {
        title: "Orders by Status",
        items: data.orders.byStatus.map((r) => ({
          label: r.key || "Unknown",
          count: r.count,
        })),
        labelKey: "label",
        valueKey: "count",
      },
    ];
  }

  return [];
};

const buildFetchParams = (moduleKey, filters, cardState = {}) => {
  const params = {
    period: filters.period,
    groupBy: filters.groupBy,
  };

  if (moduleKey === "orders" && cardState.status) {
    params.orderStatus = cardState.status;
  }
  if (moduleKey === "products" && cardState.status !== "") {
    params.status = cardState.status;
  }
  if (moduleKey === "blogs" && cardState.status) {
    params.status = cardState.status;
  }
  if (moduleKey === "projects" && cardState.status !== "") {
    params.status = cardState.status;
  }
  if (moduleKey === "contracts") {
    if (cardState.type) params.type = cardState.type;
    if (cardState.status) params.status = cardState.status;
  }
  if (moduleKey === "pricing" && cardState.type) {
    params.type = cardState.type;
  }

  return params;
};

export default function DashboardAnalyticsPanel({
  moduleKey,
  onModuleChange,
  cardFilters = {},
}) {
  const {
    getStats,
    getDesignsAnalytics,
    getInteriorDesignsAnalytics,
    getShopAnalytics,
    getBlogsAnalytics,
    getProjectsAnalytics,
    getContractsAnalytics,
    getPricingAnalytics,
  } = useDashboardStats();

  const meta = getModuleMeta(moduleKey);
  const cardState = cardFilters[moduleKey] || {};
  const cardPeriod = cardState.period;
  const cardStatus = cardState.status;
  const cardType = cardState.type;

  const [filters, setFilters] = useState({
    period: cardPeriod && cardPeriod !== "all" ? cardPeriod : "this_month",
    groupBy: "monthly",
  });
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchers = useMemo(
    () => ({
      users: getStats,
      orders: getShopAnalytics,
      products: getShopAnalytics,
      designs: getDesignsAnalytics,
      interiors: getInteriorDesignsAnalytics,
      blogs: getBlogsAnalytics,
      projects: getProjectsAnalytics,
      contracts: getContractsAnalytics,
      pricing: getPricingAnalytics,
    }),
    [
      getStats,
      getShopAnalytics,
      getDesignsAnalytics,
      getInteriorDesignsAnalytics,
      getBlogsAnalytics,
      getProjectsAnalytics,
      getContractsAnalytics,
      getPricingAnalytics,
    ],
  );

  const loadAnalytics = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const fetcher = fetchers[moduleKey];
      if (!fetcher) return;
      const params = buildFetchParams(
        moduleKey,
        filters,
        { period: cardPeriod, status: cardStatus, type: cardType },
      );
      const result = await fetcher(params);
      setData(result);
    } catch {
      setError("Failed to load chart data.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchers, moduleKey, filters, cardPeriod, cardStatus, cardType]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    if (cardPeriod && cardPeriod !== "all") {
      setFilters((prev) => ({ ...prev, period: cardPeriod }));
    }
  }, [moduleKey, cardPeriod]);

  const charts = buildChartsForModule(moduleKey, data);
  const comparison = getComparisonForModule(moduleKey, data);
  const breakdowns = getBreakdownsForModule(moduleKey, data);

  const summaryMetrics = useMemo(() => {
    if (!data) return [];
    const s = data.summary || {};

    if (moduleKey === "users") {
      return [
        { label: "Total Users", value: formatNumber(data.summary?.users ?? s.total) },
        {
          label: "New in Period",
          value: formatNumber(data.comparison?.users?.current?.total),
        },
      ];
    }

    if (moduleKey === "orders") {
      const o = data.orders || {};
      return [
        { label: "Total Orders", value: formatNumber(o.total) },
        { label: "Gross Merchandise Value", value: formatNumber(o.gmv) },
        { label: "Approved", value: formatNumber(o.approvedCount) },
      ];
    }

    return [
      {
        label: "Total",
        value: formatNumber(s.total ?? data.comparison?.current?.total),
      },
    ];
  }, [data, moduleKey]);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{meta.label} Analytics</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Trend charts and breakdowns for {meta.label.toLowerCase()}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {MODULES.map((mod) => (
            <button
              key={mod.key}
              type="button"
              onClick={() => onModuleChange(mod.key)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                moduleKey === mod.key
                  ? "text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
              }`}
              style={
                moduleKey === mod.key
                  ? { backgroundColor: mod.accent }
                  : undefined
              }
            >
              {mod.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">
              Period
            </label>
            <select
              className={selectClass}
              value={filters.period}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, period: e.target.value }))
              }
            >
              {PERIOD_OPTIONS.filter((o) => o.value !== "custom").map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">
              Group By
            </label>
            <select
              className={selectClass}
              value={filters.groupBy}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, groupBy: e.target.value }))
              }
            >
              {GROUP_BY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
      </div>

      {!!summaryMetrics.length && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {summaryMetrics.map((metric) => (
            <div
              key={metric.label}
              className="rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {metric.label}
              </p>
              <p
                className="mt-2 text-2xl font-bold tabular-nums"
                style={{ color: meta.accent }}
              >
                {isLoading ? "..." : metric.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {comparison && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
          {Array.isArray(comparison) ? (
            comparison.map((c) => (
              <ComparisonCard
                key={c.title}
                title={c.title}
                comparison={c.data}
                isLoading={isLoading}
                accent={meta.accent}
              />
            ))
          ) : (
            <ComparisonCard
              title="Period Comparison"
              comparison={comparison}
              isLoading={isLoading}
              accent={meta.accent}
            />
          )}
        </div>
      )}

      {charts.length ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
          {charts.map((chart) => (
            <ChartPanel
              key={chart.title}
              title={chart.title}
              chartPayload={chart.chart}
              isLoading={isLoading}
              color={chart.color || meta.accent}
              type={chart.type || "line"}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-400 mb-6">
          {isLoading ? "Loading charts..." : "No chart data available for this module."}
        </div>
      )}

      {!!breakdowns.length && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {breakdowns.map((b) => (
            <BreakdownPanel
              key={b.title}
              title={b.title}
              items={b.items}
              labelKey={b.labelKey}
              valueKey={b.valueKey || "count"}
              isLoading={isLoading}
              color={b.color || meta.accent}
            />
          ))}
        </div>
      )}
    </div>
  );
}
